import styled, { keyframes, css } from "styled-components";
import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUploading from 'react-images-uploading';
import { HiOutlinePhotograph, HiOutlineCloudUpload, HiOutlineTrash, HiOutlineRefresh } from 'react-icons/hi';
import { IoClose, IoCheckmarkCircle, IoImageOutline } from 'react-icons/io5';
import { updateUserImg } from "../../services/api";
import { toast } from "react-toastify";
import CryptoJS from 'crypto-js';
import { getStoredUser } from '../../utils/safeStorage';

// ============================================
// ANIMAÇÕES
// ============================================
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const borderDash = keyframes`
  0% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 20; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(8px);
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1001;
  width: 95%;
  max-width: 480px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  animation: ${slideUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  position: relative;
  padding: 28px 32px 20px;
  text-align: center;
  background: linear-gradient(135deg, rgba(49, 140, 213, 0.06) 0%, rgba(79, 172, 254, 0.03) 100%);
  border-bottom: 1px solid #f1f5f9;
`;

const HeaderIcon = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgb(49, 140, 213) 0%, rgb(79, 172, 254) 100%);
  border-radius: 16px;
  color: white;
  box-shadow: 0 8px 20px -4px rgba(49, 140, 213, 0.4);

  svg {
    width: 28px;
    height: 28px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fee2e2;
    border-color: #fecaca;
    color: #dc2626;
    transform: rotate(90deg);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Title = styled.h2`
  margin: 0 0 4px 0;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.5px;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #64748b;
`;

const ModalBody = styled.div`
  padding: 28px 32px;
`;

const DropZone = styled.div`
  position: relative;
  min-height: 220px;
  border: 2px dashed ${({ $isDragging, $hasImage }) =>
    $isDragging ? 'rgb(49, 140, 213)' : $hasImage ? 'transparent' : '#d1d5db'};
  border-radius: 16px;
  background: ${({ $isDragging, $hasImage }) =>
    $isDragging
      ? 'rgba(49, 140, 213, 0.08)'
      : $hasImage
        ? 'transparent'
        : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;

  ${({ $isDragging }) => $isDragging && css`
    animation: ${pulse} 1s ease infinite;
    border-color: rgb(49, 140, 213);
  `}

  &:hover {
    border-color: ${({ $hasImage }) => $hasImage ? 'transparent' : 'rgb(49, 140, 213)'};
    background: ${({ $hasImage }) => $hasImage ? 'transparent' : 'rgba(49, 140, 213, 0.04)'};
  }
`;

const DropZoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
`;

const UploadIcon = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(49, 140, 213, 0.1) 0%, rgba(79, 172, 254, 0.05) 100%);
  border-radius: 50%;
  color: rgb(49, 140, 213);
  animation: ${float} 3s ease-in-out infinite;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const DropText = styled.div`
  text-align: center;
`;

const DropTextMain = styled.p`
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
`;

const DropTextSub = styled.p`
  margin: 0;
  font-size: 13px;
  color: #9ca3af;

  span {
    color: rgb(49, 140, 213);
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const FileSpecs = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
`;

const FileSpec = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DraggingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(49, 140, 213, 0.1);
  border-radius: 14px;
  z-index: 2;
`;

const DraggingText = styled.p`
  margin: 12px 0 0 0;
  font-size: 16px;
  font-weight: 600;
  color: rgb(49, 140, 213);
`;

// Preview da Imagem
const PreviewContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: 12px;
  object-fit: contain;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const PreviewActions = styled.div`
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
`;

const PreviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &.change {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;

    &:hover {
      background: #f8fafc;
      border-color: #94a3b8;
    }
  }

  &.remove {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;

    &:hover {
      background: #fee2e2;
    }
  }
`;

// Erros
const ErrorContainer = styled.div`
  margin-top: 12px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ErrorIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fee2e2;
  border-radius: 50%;
  color: #dc2626;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ErrorText = styled.span`
  font-size: 13px;
  color: #dc2626;
  font-weight: 500;
`;

// Footer
const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 32px 28px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  padding: 0 22px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const CancelButton = styled(Button)`
  background: white;
  color: #64748b;
  border: 2px solid #e2e8f0;

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #475569;
  }
`;

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, rgb(49, 140, 213) 0%, rgb(79, 172, 254) 100%);
  color: white;
  border: none;
  min-width: 150px;
  position: relative;
  overflow: hidden;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px -4px rgba(49, 140, 213, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &.loading {
    pointer-events: none;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
      background-size: 200% 100%;
      animation: ${shimmer} 1.5s infinite;
    }
  }
`;

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
const ModalUserImg = ({ setModalImg, showModalImg, userID }) => {
  const { t } = useTranslation('common');
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const maxNumber = 1;
  const maxMbFileSize = 2 * 1024 * 1024; // 2MB

  const secretKey = 'my-custom-secret-key:1af0addcce4b56f753ed10c285667ad031bae57af19747f576b05312b8cf85be';

  const onChange = (imageList) => {
    setImages(imageList);
    setError(null);
  };

  const handleSaveImg = async () => {
    if (images.length === 0) {
      setError(t('editImage.errors.required'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await updateUserImg(images);

      if (res.status === 200) {
        // Atualizar localStorage
        let existing = getStoredUser();
        existing['user_img'] = [res.data.image];

        const loggedUserEncrypted = CryptoJS.AES.encrypt(JSON.stringify(existing), secretKey).toString();
        localStorage.setItem('user', loggedUserEncrypted);

        toast.success(t('editImage.success'), { autoClose: 3000 });
        closeModal();
        window.location.reload();
      } else {
        toast.error(res.data.msg || t('editImage.errors.uploadFailed'), { autoClose: 5000 });
      }
    } catch (err) {
      toast.error(t('editImage.errors.uploadFailed'), { autoClose: 5000 });
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setModalImg(!showModalImg);
    setImages([]);
    setError(null);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const isOpen = !showModalImg;

  return (
    <Overlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <CloseButton onClick={closeModal} type="button">
            <IoClose />
          </CloseButton>
          <HeaderIcon>
            <HiOutlinePhotograph />
          </HeaderIcon>
          <Title>{t('editImage.title')}</Title>
          <Subtitle>{t('editImage.subtitle')}</Subtitle>
        </ModalHeader>

        <ModalBody>
          <ImageUploading
            multiple={false}
            value={images}
            onChange={onChange}
            maxNumber={maxNumber}
            maxFileSize={maxMbFileSize}
            acceptType={["jpg", "jpeg", "png", "webp"]}
            dataURLKey="data_url"
          >
            {({
              imageList,
              onImageUpload,
              onImageUpdate,
              onImageRemove,
              isDragging,
              dragProps,
              errors: uploadErrors
            }) => (
              <div>
                <DropZone
                  $isDragging={isDragging}
                  $hasImage={imageList.length > 0}
                  onClick={imageList.length === 0 ? onImageUpload : undefined}
                  {...dragProps}
                >
                  {isDragging && imageList.length === 0 && (
                    <DraggingOverlay>
                      <UploadIcon style={{ animation: 'none' }}>
                        <HiOutlineCloudUpload />
                      </UploadIcon>
                      <DraggingText>{t('editImage.dropHere')}</DraggingText>
                    </DraggingOverlay>
                  )}

                  {imageList.length === 0 && !isDragging && (
                    <DropZoneContent>
                      <UploadIcon>
                        <IoImageOutline />
                      </UploadIcon>
                      <DropText>
                        <DropTextMain>{t('editImage.dragDropHint')}</DropTextMain>
                        <DropTextSub>
                          {t('editImage.dragDropOr')} <span>{t('editImage.browseFiles')}</span>
                        </DropTextSub>
                      </DropText>
                      <FileSpecs>
                        <FileSpec>{t('editImage.fileTypes')}</FileSpec>
                        <FileSpec>{t('editImage.maxSize')}</FileSpec>
                      </FileSpecs>
                    </DropZoneContent>
                  )}

                  {imageList.length > 0 && (
                    <PreviewContainer>
                      <PreviewImage src={imageList[0]['data_url']} alt="Preview" />
                      <PreviewActions>
                        <PreviewButton className="change" onClick={() => onImageUpdate(0)}>
                          <HiOutlineRefresh />
                          {t('editImage.changeImage')}
                        </PreviewButton>
                        <PreviewButton className="remove" onClick={() => onImageRemove(0)}>
                          <HiOutlineTrash />
                          {t('editImage.removeImage')}
                        </PreviewButton>
                      </PreviewActions>
                    </PreviewContainer>
                  )}
                </DropZone>

                {/* Erros de Upload */}
                {uploadErrors && (
                  <>
                    {uploadErrors.maxNumber && (
                      <ErrorContainer>
                        <ErrorIcon><IoClose /></ErrorIcon>
                        <ErrorText>{t('editImage.errors.maxNumber')}</ErrorText>
                      </ErrorContainer>
                    )}
                    {uploadErrors.acceptType && (
                      <ErrorContainer>
                        <ErrorIcon><IoClose /></ErrorIcon>
                        <ErrorText>{t('editImage.errors.invalidType')}</ErrorText>
                      </ErrorContainer>
                    )}
                    {uploadErrors.maxFileSize && (
                      <ErrorContainer>
                        <ErrorIcon><IoClose /></ErrorIcon>
                        <ErrorText>{t('editImage.errors.maxSize')}</ErrorText>
                      </ErrorContainer>
                    )}
                  </>
                )}

                {/* Erro customizado */}
                {error && (
                  <ErrorContainer>
                    <ErrorIcon><IoClose /></ErrorIcon>
                    <ErrorText>{error}</ErrorText>
                  </ErrorContainer>
                )}
              </div>
            )}
          </ImageUploading>
        </ModalBody>

        <ModalFooter>
          <CancelButton type="button" onClick={closeModal}>
            <IoClose />
            {t('editImage.cancel')}
          </CancelButton>
          <SaveButton
            type="button"
            onClick={handleSaveImg}
            className={isLoading ? 'loading' : ''}
            disabled={isLoading || images.length === 0}
          >
            <IoCheckmarkCircle />
            {isLoading ? t('messages.saving') : t('editImage.save')}
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
};

export default ModalUserImg;
